import Close from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import useEntryWorkspaceMutations from 'api/operation/entry/useEntryWorkspaceMutations';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';

import ManualSelectShppingCart from './ManualSelectShppingCart';
import ScanShoppingCart from './ScanShoppingCart';

const ShoppingCartAddModal = ({
  open,
  onClose,
  operationGuid,
  onCompleted,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = operationSettings?.defaultZone?.key;
  const mutations = useEntryWorkspaceMutations();

  const [selectedCartGuid, setSelectedCartGuid] = useState('');
  const [emptyError, setEmptyError] = useState(false);
  const [resolvedCart, setResolvedCart] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSectionGuid, setSelectedSectionGuid] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('guid'); // guid | section
  const [entryMode, setEntryMode] = useState('manual'); // manual | scan
  const [availableShppingCartList, setAvailableShppingCartList] = useState([]);
  const [getAvailableShoppingCartLoading, setGetAvailableShoppingCartLoading] =
    useState(false);

  const loadingRef = useRef(false);
  const { getAvailableShoppingCartList } = mutations;

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (!open || !zoneGuid) return;

    let cancelled = false;
    setGetAvailableShoppingCartLoading(true);

    getAvailableShoppingCartList(zoneGuid).then(list => {
      if (cancelled) return;
      setAvailableShppingCartList(Array.isArray(list) ? list : []);
      setGetAvailableShoppingCartLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, zoneGuid, getAvailableShoppingCartList]);

  useEffect(() => {
    if (!open) {
      setSelectedCartGuid('');
      setResolvedCart(null);
      setSections([]);
      setSelectedSectionGuid('');
      setStep('guid');
      setEntryMode('manual');
      setEmptyError(false);
    }
  }, [open]);

  const goToSectionStep = useCallback(
    async (cartGuid, cart) => {
      setSelectedCartGuid(cartGuid);
      setResolvedCart(cart);

      const sectionRes = await mutations.getZoneSections(zoneGuid);
      setSections(
        Array.isArray(sectionRes?.list) ? sectionRes.list : sectionRes ?? [],
      );

      setSelectedSectionGuid('');
      setStep('section');
    },
    [mutations, zoneGuid],
  );

  const continueManual = async () => {
    const guid = selectedCartGuid.trim();

    if (!guid) {
      setEmptyError(true);
      return;
    }

    if (loadingRef.current || getAvailableShoppingCartLoading) return;

    const selectedCart = (availableShppingCartList ?? []).find(
      cart => cart.key === guid,
    );

    setLoading(true);
    await goToSectionStep(guid, selectedCart ?? { key: guid, value: guid });
    setLoading(false);
  };

  const resolveScannedCart = useCallback(
    async scannedGuid => {
      const guid = scannedGuid.trim();

      if (!guid || loadingRef.current) return;

      setLoading(true);

      const details = await mutations.getScannerDetails(guid);

      if (!details) {
        setLoading(false);
        return;
      }

      const first = Array.isArray(details?.value)
        ? details.value[0]
        : Array.isArray(details)
        ? details[0]
        : details;

      const cartGuid = first?.guid || first?.key || guid;
      await goToSectionStep(cartGuid, first);
      setLoading(false);
    },
    [mutations, goToSectionStep],
  );

  const insertBasket = async () => {
    if (!selectedSectionGuid) return;
    setLoading(true);
    const ok = await mutations.insertShoppingCart({
      operationGuid,
      shoppingCartGuid: selectedCartGuid.trim(),
      sectionGuid: selectedSectionGuid,
    });
    setLoading(false);
    if (ok) {
      onCompleted?.();
      onClose?.();
    }
  };

  const switchEntryMode = mode => {
    setEntryMode(mode);
    setSelectedCartGuid('');
    setEmptyError(false);
  };

  const handleSelectCartGuid = guid => {
    setSelectedCartGuid(guid);
    setEmptyError(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ direction: 'rtl' }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        افزودن سبد
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {step === 'guid' ? (
          <Stack gap={2} pt={1}>
            <Stack direction="row" gap={1}>
              <Button
                fullWidth
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
                variant={entryMode === 'manual' ? 'contained' : 'outlined'}
                startIcon={<KeyboardIcon />}
                onClick={() => switchEntryMode('manual')}
              >
                ورود دستی
              </Button>
              <Button
                fullWidth
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
                variant={entryMode === 'scan' ? 'contained' : 'outlined'}
                startIcon={<QrCodeScannerIcon />}
                onClick={() => switchEntryMode('scan')}
              >
                اسکن بارکد
              </Button>
            </Stack>

            {entryMode === 'manual' ? (
              <ManualSelectShppingCart
                availableShppingCartList={availableShppingCartList}
                selectedCartGuid={selectedCartGuid}
                emptyError={emptyError}
                loading={getAvailableShoppingCartLoading}
                setSelectedCartGuid={handleSelectCartGuid}
              />
            ) : (
              <ScanShoppingCart
                onScan={resolveScannedCart}
                disabled={loading}
              />
            )}
          </Stack>
        ) : (
          <Stack gap={1.5} pt={1}>
            <Typography variant="body2">
              سبد انتخاب شده:{' '}
              {resolvedCart?.value || resolvedCart?.title || selectedCartGuid}
            </Typography>
            <Typography>برای ادامه، طبقه‌ را انتخاب کنید.</Typography>
            {loading && !sections.length ? (
              <CircularProgress size={22} />
            ) : (
              <FormControl fullWidth>
                <InputLabel id="section-select-label">طبقه</InputLabel>
                <Select
                  labelId="section-select-label"
                  label="طبقه"
                  value={selectedSectionGuid}
                  disabled={loading}
                  onChange={e => setSelectedSectionGuid(e.target.value)}
                >
                  {sections.map(section => (
                    <MenuItem
                      key={section.guid}
                      value={section.guid}
                      sx={{ direction: 'rtl' }}
                    >
                      {section.display}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 1, p: 2 }}>
        <Button onClick={onClose}>انصراف</Button>
        {step === 'guid' ? (
          entryMode === 'manual' ? (
            <LoadingButton
              loading={loading}
              variant="contained"
              disabled={getAvailableShoppingCartLoading}
              onClick={continueManual}
            >
              ادامه
            </LoadingButton>
          ) : loading ? (
            <LoadingButton loading variant="contained" disabled>
              در حال پردازش…
            </LoadingButton>
          ) : null
        ) : (
          <LoadingButton
            loading={loading}
            variant="contained"
            disabled={!selectedSectionGuid}
            onClick={insertBasket}
          >
            افزودن
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShoppingCartAddModal;
